<template>
  <section v-if="contextData">
    <div class="container-wrap py-9 tablet:py-12">
      <h2 class="text-solution-h2 mb-6 tablet:line-clamp-2">
        {{ contextData.title }}
      </h2>
      <figure
        class="relative block aspect-video w-full overflow-hidden rounded"
      >
        <img
          :src="contextThumbnail"
          :alt="contextData.title || contextData.description"
          loading="lazy"
          class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
        />
      </figure>
      <p
        class="my-6 w-full text-sm text-gray-600 tablet:line-clamp-4 tablet:text-base"
      >
        {{ contextData.description }}
      </p>
      <div class="flex flex-wrap">
        <TheButton
          v-if="button01?.text"
          :id="button01?.trackingCode"
          :to="provideFormatUrl(button01.link)"
          :title="button01.text"
          class="mb-3 w-full tablet:mr-2.5 tablet:w-[180px] desktop:mr-6"
        >
          <span class="text-sm font-bold">
            {{ button01.text }}
          </span>
        </TheButton>
        <TheButton
          v-if="button02?.text"
          :id="button02.trackingCode"
          :to="provideFormatUrl(button02.link)"
          :title="button02.text"
          type="outline"
          class="mb-3 w-full bg-white tablet:w-[180px]"
        >
          <span class="text-sm font-bold">
            {{ button02.text }}
          </span>
        </TheButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IButton {
    text: string;
    link: string;
    trackingCode?: string;
  }

  interface IArchitectureData {
    title: string;
    description: string;
    buttons: IButton[];
    thumbnail: string;
  }

  interface IArchitecture {
    component: 'architecture';
    meta: {
      navigable?: string;
    };
    data: IArchitectureData;
  }

  type IProps = ISuiteProps<IArchitecture>;

  const props = defineProps<IProps>();

  const { provideFormatUrl } = useFormatUrl();
  const { provideResponsiveImgUrl } = useSolutionsData();

  const contextData = computed((): IArchitectureData => props.suiteData.data);

  const contextThumbnail = computed((): string => {
    const { thumbnail } = contextData.value;
    if (!thumbnail) return '';

    if (props.urlConnector === 'solution') {
      return provideResponsiveImgUrl(thumbnail);
    }
    return thumbnail;
  });

  const button01 = computed(
    (): IButton | undefined => contextData.value.buttons[0]
  );

  const button02 = computed(
    (): IButton | undefined => contextData.value.buttons[1]
  );

  export type { IArchitecture, IArchitectureData, IButton };
</script>
